import { useEffect, useState } from 'react'
import { getSavedAuthData } from '../api/axios/interceptor'

/**
 * L'URL affichable d'un fichier du dossier uploads, derrière le JWT.
 *
 * Le service statique public de /uploads a été supprimé à dessein : le
 * dossier mêle les avatars aux fichiers MÉDICAUX (recadrages de bandelettes,
 * rapports PDF), et tout y est servi par /api/uploads/* sous jeton. Une
 * balise <img> ne sait pas porter d'en-tête Authorization : les avatars
 * construisaient encore l'ancienne URL publique et cassaient partout —
 * l'image s'affichait le temps de l'aperçu local, puis disparaissait au
 * rechargement du profil.
 *
 * Ce hook fait le pont : il télécharge le fichier AVEC le jeton et rend une
 * URL de blob. Un cache de module évite de retélécharger le même avatar à
 * chaque re-rendu de la barre latérale ; les URLs de blob y restent vivantes
 * pour la durée de la session, ce que quelques avatars supportent sans
 * peine.
 */
const cache = new Map<string, string>()
const pending = new Map<string, Promise<string | null>>()

export function useAuthedImage(uiPath: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(uiPath ? cache.get(uiPath) ?? null : null)

  useEffect(() => {
    if (!uiPath) {
      setUrl(null)
      return
    }
    const cached = cache.get(uiPath)
    if (cached) {
      setUrl(cached)
      return
    }
    let cancelled = false
    const base = process.env.REACT_APP_API_DOMAIN ?? ''
    const fetchOnce = pending.get(uiPath) ?? (async () => {
      try {
        const { accessToken } = getSavedAuthData()
        const clean = uiPath.startsWith('/') ? uiPath : `/${uiPath}`
        const response = await fetch(`${base}/api${clean}`, {
          headers: { Authorization: accessToken || '' },
        })
        if (!response.ok) return null
        const blobUrl = URL.createObjectURL(await response.blob())
        cache.set(uiPath, blobUrl)
        return blobUrl
      } catch {
        return null
      } finally {
        pending.delete(uiPath)
      }
    })()
    pending.set(uiPath, fetchOnce)
    fetchOnce.then((result) => { if (!cancelled) setUrl(result) })
    return () => { cancelled = true }
  }, [uiPath])

  return url
}

/** Invalide l'entrée après un nouvel upload, pour réafficher sans recharger. */
export function invalidateAuthedImage(uiPath: string | null | undefined): void {
  if (!uiPath) return
  const cached = cache.get(uiPath)
  if (cached) URL.revokeObjectURL(cached)
  cache.delete(uiPath)
}
